/**
 * Step3Page - Página principal P4 Exportar
 *
 * F5.04b2: Conectada a ExportService vía useExportWizard.
 * Orquesta exportación en formatos: XLSX, ODS, GeoJSON, CSV, KML, SHP.
 *
 * @version 2.0.0
 * @date 2026-02-24
 */

import React, { useEffect, useMemo, useState } from 'react';
import { InfoBanner, BannerStrong } from '../shell/InfoBanner';
import { getCodigoINE } from '@/lib/codigosINEUnificado';
import {
  loadBoundaryByINE,
  getBoundaryLoadStatus,
  textoSinValidarContorno,
  type BoundaryLoadStatus,
} from '@/services/boundaries/MunicipalBoundaryService';
import type { ProcessedInfrastructure } from '@/types/processing';
import type { ResultadoCotejo } from '@/services/cotejo/types';
import type { ExportFormat, DocumentMetadata, ProcessedRecord } from '@/types/export.types';
import { TipologiaPTEL } from '@/types/tipologia';
import { useExportWizard, type ExportPhase } from '../hooks/useExportWizard';
import { useAudit } from '@/hooks/useAudit';
import { usePermissions } from '@/hooks/usePermissions';
import { porcentajeSinEsconder } from '@/utils/porcentajeSinEsconder';
import { SummaryPanel } from './SummaryPanel';
import type { SummaryPanelContentProps, DetailStats, ScoreDistribution, TypologyData } from './SummaryPanelContent';
import { ProvinceMetricsPanel, buildMetricsFromInfras } from './ProvinceMetricsPanel';
import { DiscrepancyPanel, buildDiscrepancyFromInfras } from './DiscrepancyPanel';
import { colors, typography, spacing, borderRadius, transitions } from '@/design-system/v2';
// SERP2: por ruta de fichero, no por el barril (HX-MOCK-FACTORY-BARREL-COUPLING).
import { SpinnerSnake } from '@/design-system/v2/components/SpinnerSnake';

const _GAP_MD = spacing[2]; // 8px — mismo que spacing[2] del DS
const GAP_SM = spacing[2]; // 6px aprox — usar spacing[2] por consistencia

// ============================================================================
// TOOLTIP — componente mínimo inline (punto 2)
// ============================================================================
const Tooltip: React.FC<{ text: string; children: React.ReactNode }> = ({ text, children }) => (
  <span style={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }}>
    {children}
    <span style={{
      position: 'absolute', bottom: '130%', left: '50%', transform: 'translateX(-50%)',
      background: colors.bg.overlay, border: `1px solid ${colors.border.DEFAULT}`,
      color: colors.fg.secondary, fontSize: '11px', lineHeight: 1.5,
      padding: '5px 9px', borderRadius: '6px',
      pointerEvents: 'none', opacity: 0, transition: `opacity ${transitions.base}`,
      zIndex: 50, minWidth: '160px', maxWidth: '260px', whiteSpace: 'normal' as React.CSSProperties['whiteSpace'],
    }} className="ptel-tooltip-text">
      {text}
    </span>
    <style>{`.ptel-tooltip-text { white-space: normal !important; }
span:hover > .ptel-tooltip-text { opacity: 1 !important; }`}</style>
  </span>
);

// ============================================================================
// TIPOS
// ============================================================================

export interface Step3PageProps {
  /** Infraestructuras procesadas por el pipeline */
  infrastructures: ProcessedInfrastructure[];
  /** Metadatos del documento fuente */
  metadata: DocumentMetadata;
  /** Resultado del cotejo de completitud (P3); opcional → informe HTML con sección de cotejo (INF-1b) */
  resultadoCotejo?: ResultadoCotejo;
  /** Callback opcional de navegación atrás (paso anterior: Coteja) */
  onBack?: () => void;
  /** P4REP: lleva al paso Valida (P2); sin él, el recuadro de discrepancias no pinta botón */
  onGoToValida?: () => void;
}

// ============================================================================
// CONFIGURACIÓN DE FORMATOS
// ============================================================================

interface FormatCardConfig {
  value: ExportFormat;
  label: string;
  icon: React.ReactNode;
  description: string;
  requiresGeo: boolean;
}

// SVG icons para los formatos de exportación (DS v2: stroke currentColor, 1.5)
const IconXlsx = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="3" width="18" height="18" rx="2"/>
    <line x1="3" y1="9" x2="21" y2="9"/><line x1="3" y1="15" x2="21" y2="15"/>
    <line x1="9" y1="9" x2="9" y2="21"/><line x1="15" y1="9" x2="15" y2="21"/>
  </svg>
);
const IconGeoJson = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z"/>
    <circle cx="12" cy="9" r="2.5"/>
  </svg>
);
const IconCsv = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
    <polyline points="14 2 14 8 20 8"/>
    <line x1="8" y1="13" x2="16" y2="13"/><line x1="8" y1="17" x2="16" y2="17"/>
  </svg>
);
const IconKml = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10"/>
    <line x1="2" y1="12" x2="22" y2="12"/>
    <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
  </svg>
);
const IconShapefile = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="12 2 2 7 12 12 22 7 12 2"/>
    <polyline points="2 17 12 22 22 17"/>
    <polyline points="2 12 12 17 22 12"/>
  </svg>
);
const IconOds = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
    <polyline points="14 2 14 8 20 8"/>
    <line x1="8" y1="13" x2="16" y2="13"/><line x1="8" y1="17" x2="12" y2="17"/>
  </svg>
);
const IconHtml = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="16 18 22 12 16 6"/>
    <polyline points="8 6 2 12 8 18"/>
  </svg>
);

const IconPdf = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
    <polyline points="14 2 14 8 20 8"/>
    <path d="M9 15v-4h1.5a1.5 1.5 0 0 1 0 3H9"/>
  </svg>
);

// Color accent por formato — alias de colors.formats.* del DS v2 (sin `pdf`: gris de reserva, DOP-6 a)
const FORMAT_ACCENT: Record<string, string> = colors.formats;

const FORMAT_CARDS: FormatCardConfig[] = [
  {
    value: 'xlsx',
    label: 'Excel (.xlsx)',
    icon: <IconXlsx />,
    description: '11 pestañas organizadas por tipología con formato condicional, filtros y columnas de auditoría (score, fuente, flag). Ideal para revisión y entrega oficial.',
    requiresGeo: false,
  },
  {
    value: 'geojson',
    label: 'GeoJSON',
    icon: <IconGeoJson />,
    description: 'Formato estándar GIS compatible con QGIS, ArcGIS Online y visores web. Geometría en WGS84 con atributos EPSG:25830. Solo incluye infraestructuras geolocalizadas.',
    requiresGeo: true,
  },
  {
    value: 'csv',
    label: 'CSV',
    icon: <IconCsv />,
    description: 'Tabla plana con separador punto y coma, codificación UTF-8 con BOM. Abre directamente en Excel sin configuración adicional. Incluye todas las tipologías en un único fichero.',
    requiresGeo: false,
  },
  {
    value: 'kml',
    label: 'KML',
    icon: <IconKml />,
    description: 'Fichero de puntos para Google Earth y Google Maps. Iconos diferenciados por tipología y descripción emergente con datos del PTEL. Solo infraestructuras con coordenadas.',
    requiresGeo: true,
  },
  {
    value: 'shapefile',
    label: 'Shapefile (.zip)',
    icon: <IconShapefile />,
    description: 'Paquete .zip con los ficheros .shp, .dbf, .prj y .cpg. Proyección UTM30 ETRS89 (EPSG:25830). Compatible con QGIS, ArcGIS y cualquier SIG de escritorio.',
    requiresGeo: true,
  },
  {
    value: 'ods',
    label: 'ODS',
    icon: <IconOds />,
    description: 'Formato ISO/IEC 26300 (OpenDocument Spreadsheet). Compatible con LibreOffice, Google Sheets y Excel. Misma estructura que el .xlsx pero sin dependencia de licencia.',
    requiresGeo: false,
  },
  {
    value: 'html',
    label: 'Informe HTML',
    icon: <IconHtml />,
    description: 'Página web autocontenida con el resumen del municipio: métricas, desgloses por confianza y tipología, y los aforos y el albergue con sus totales. Legible en cualquier navegador sin instalar nada.',
    requiresGeo: false,
  },
  {
    // AFOTOT-a2b · DOP-1 a: el informe PDF ya se puede pedir desde P4.
    value: 'pdf',
    label: 'Informe PDF',
    icon: <IconPdf />,
    description: 'Documento A4 listo para imprimir o archivar: métricas, desgloses por confianza y tipología, y los aforos y el albergue con sus totales, con páginas numeradas.',
    requiresGeo: false,
  },
];

// Formatos en desarrollo — informativos, no seleccionables
const COMING_SOON_CARDS: Array<{ value: string; label: string; icon: React.ReactNode; description: string }> = [
  {
    value: 'qgs',
    label: 'Proyecto QGIS (.qgs)',
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="3" width="18" height="18" rx="2"/>
        <path d="M3 9h18M9 21V9"/>
        <circle cx="15" cy="15" r="2"/>
      </svg>
    ),
    description: 'Proyecto QGIS preconfigurado con capas temáticas, simbología PTEL y conexiones WMS a PNOA, Catastro y DERA. Listo para abrir sin configuración adicional.',
  },
  {
    value: 'gpkg',
    label: 'GeoPackage (.gpkg)',
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <ellipse cx="12" cy="5" rx="9" ry="3"/>
        <path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3"/>
        <path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5"/>
      </svg>
    ),
    description: 'Estándar OGC — un único archivo con todas las capas, geometría y metadatos en EPSG:25830. Alternativa moderna al Shapefile sin límite de nombre de campo.',
  },
];

// ============================================================================
// MAPEO TIPOLOGÍAS → CLAVES SUMMARY CONTENT (19 categorías macro)
// ============================================================================

const TIPOLOGIA_MAP: Record<TipologiaPTEL, string> = {
  SANITARIO: 'sanitario',
  EDUCATIVO: 'educativo',
  POLICIAL: 'seguridad',
  BOMBEROS: 'servicios',
  CULTURAL: 'cultural',
  RELIGIOSO: 'cultural',
  DEPORTIVO: 'deportivo',
  MUNICIPAL: 'administrativo',
  SOCIAL: 'servicios',
  COMBUSTIBLE: 'servicios',
  EMERGENCIAS: 'servicios',
  HIDRAULICO: 'servicios',
  ENERGIA: 'servicios',
  TELECOM: 'servicios',
  VIAL: 'servicios',
  INDUSTRIAL: 'servicios',
  RSU: 'servicios',
  PUERTO: 'servicios',
  GENERICO: 'otros',
};

const TIPOLOGIA_NOMBRES: Record<string, string> = {
  sanitario: 'Sanitario',
  educativo: 'Educativo',
  seguridad: 'Seguridad',
  servicios: 'Servicios / Emergencias',
  cultural: 'Cultural',
  deportivo: 'Deportivo',
  administrativo: 'Administrativo',
  otros: 'Otros',
};

function computeScoreDistribution(records: ProcessedRecord[]): ScoreDistribution {
  if (records.length === 0) return { high: 0, medium: 0, low: 0, critical: 0 };
  let high = 0, medium = 0, low = 0, critical = 0;
  for (const r of records) {
    if (r.score >= 90) high++;
    else if (r.score >= 70) medium++;
    else if (r.score >= 50) low++;
    else critical++;
  }
  const total = records.length;
  return {
    high: Math.round((high / total) * 100),
    medium: Math.round((medium / total) * 100),
    low: Math.round((low / total) * 100),
    critical: Math.round((critical / total) * 100),
  };
}

function computeTypologies(records: ProcessedRecord[]): TypologyData[] {
  const totals = new Map<string, { total: number; geo: number }>();
  for (const r of records) {
    const key = TIPOLOGIA_MAP[r.tipologia] ?? 'otros';
    const entry = totals.get(key) ?? { total: 0, geo: 0 };
    entry.total++;
    if (r.geocodificado || (r.xUtm30 !== 0 && r.yUtm30 !== 0)) entry.geo++;
    totals.set(key, entry);
  }
  return Array.from(totals.entries())
    .map(([key, { total, geo }]) => ({
      key,
      nombre: TIPOLOGIA_NOMBRES[key] ?? key,
      geolocalizadas: geo,
      total,
    }))
    .sort((a, b) => b.total - a.total);
}

// ============================================================================
// ESTILOS
// ============================================================================

const S = {
  container: {
    fontFamily: typography.fontFamily.sans,
    color: colors.fg.primary,
    minHeight: '100vh',
    background: colors.bg.primary,
    paddingBottom: '100px',
  } as React.CSSProperties,
  main: {
    padding: `${spacing[5]} ${spacing[6]}`,
    margin: '0 56px',
  } as React.CSSProperties,
  pageTitle: {
    fontSize: typography.fontSize.pageTitle,
    fontWeight: typography.fontWeight.semibold,
    marginBottom: spacing[2],
    color: colors.fg.primary,
  } as React.CSSProperties,
  pageSubtitle: {
    fontSize: typography.fontSize.base,
    color: colors.fg.muted,
    marginBottom: spacing[6],
  } as React.CSSProperties,

  // Sección de formatos
  section: {
    marginBottom: spacing[6],
  } as React.CSSProperties,
  sectionTitle: {
    fontSize: typography.fontSize.base,
    fontWeight: typography.fontWeight.semibold,
    marginBottom: spacing[3],
    color: colors.fg.primary,
  } as React.CSSProperties,
  formatGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))',
    gap: '12px',
  } as React.CSSProperties,

  // Barra de descarga — sticky al fondo del scroll
  downloadBar: {
    display: 'grid',
    gridTemplateColumns: '1fr auto 1fr', // centro fijo, botón extremo der
    alignItems: 'center',
    padding: `${spacing[4]} ${spacing[5]}`,
    background: colors.bg.secondary,
    border: `1px solid ${colors.border.DEFAULT}`,
    borderRadius: borderRadius.xl,
    position: 'sticky',
    bottom: 0,
    zIndex: 10,
    backdropFilter: 'blur(8px)',
    boxShadow: '0 -4px 20px rgba(0,0,0,0.30)',
    marginTop: spacing[4],
  } as React.CSSProperties,
  downloadInfo: {
    fontSize: typography.fontSize.base, // 14px equilibrado
    color: colors.fg.secondary,
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
  } as React.CSSProperties,
  downloadBtn: {
    padding: `${spacing[2]} ${spacing[5]}`,
    borderRadius: borderRadius.lg,
    border: 'none',
    fontFamily: typography.fontFamily.sans,
    fontWeight: typography.fontWeight.semibold,
    fontSize: typography.fontSize.base,
    cursor: 'pointer',
    transition: `opacity ${transitions.base}`,
  } as React.CSSProperties,

  // Status
  statusBar: {
    padding: `${spacing[3]} ${spacing[4]}`,
    borderRadius: borderRadius.lg,
    marginBottom: spacing[4],
    fontSize: typography.fontSize.xs,
  } as React.CSSProperties,

  // Resultados
  resultsList: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: GAP_SM,
    marginTop: spacing[4],
  } as React.CSSProperties,
  resultItem: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: `${spacing[2]} ${spacing[3]}`,
    background: colors.bg.secondary,
    borderRadius: borderRadius.lg,
    fontSize: typography.fontSize.xs,
  } as React.CSSProperties,
} as const;

// ============================================================================
// SUB-COMPONENTES
// ============================================================================

/** Tarjeta de selección de formato (activo o en desarrollo) */
const FormatCard: React.FC<{
  config: FormatCardConfig;
  selected: boolean;
  disabled: boolean;
  totalCount: number;
  geoCount: number;
  onToggle: () => void;
  comingSoon?: boolean;
}> = ({ config, selected, disabled, totalCount, geoCount, onToggle, comingSoon }) => {
  const badgeCount = config.requiresGeo ? geoCount : totalCount;
  const badgeLabel = config.requiresGeo ? 'con coords' : 'registros';

  const accent = FORMAT_ACCENT[config.value] ?? '#94A3B8';

  const cardStyle: React.CSSProperties = {
    padding: spacing[5],
    borderRadius: borderRadius.xl,
    border: `1.5px solid ${
      comingSoon ? colors.border.light :
      selected ? accent : colors.border.DEFAULT
    }`,
    background: comingSoon
      ? 'rgba(255,255,255,0.02)'
      : selected ? `${accent}12` : colors.bg.secondary,
    cursor: (disabled || comingSoon) ? 'not-allowed' : 'pointer',
    opacity: comingSoon ? 0.5 : disabled ? 0.4 : 1,
    transition: `border-color ${transitions.base}, background ${transitions.base}`,
    textAlign: 'left',
    position: 'relative' as const,
    // layout columna: alinea badge inferior en todas las tarjetas
    display: 'flex',
    flexDirection: 'column' as const,
    height: '100%',
    boxSizing: 'border-box' as const,
  };

  return (
    <button
      type="button"
      style={cardStyle}
      onClick={(disabled || comingSoon) ? undefined : onToggle}
      disabled={disabled || comingSoon}
      aria-pressed={selected}
      data-testid={`format-card-${config.value}`}
    >
      {/* Fila superior: icono + nombre del formato en la misma línea */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '8px', gap: '8px' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', minWidth: 0 }}>
          <div style={{ flexShrink: 0, marginTop: '1px', color: comingSoon ? colors.fg.subtle : (selected ? accent : colors.fg.secondary) }}>
            {config.icon}
          </div>
          <span style={{
            fontWeight: typography.fontWeight.semibold,
            fontSize: '14px',
            color: comingSoon ? colors.fg.muted : colors.fg.primary,
            lineHeight: '1.35',
          }}>
            {config.label}
          </span>
        </div>
        {comingSoon && (
          <span style={{
            fontSize: typography.fontSize['2xs'],
            fontWeight: typography.fontWeight.semibold,
            textTransform: 'uppercase' as const,
            letterSpacing: '0.5px',
            padding: '2px 6px',
            borderRadius: borderRadius.sm,
            background: colors.warningMuted,
            color: colors.warning,
            border: `1px solid ${colors.warning}40`,
            flexShrink: 0,
          }}>En desarrollo</span>
        )}
      </div>
      <div style={{
        fontSize: typography.fontSize.sm,
        color: colors.fg.muted,
        lineHeight: 1.6,
        marginTop: '6px',
        flex: 1, // empuja el badge al fondo de la tarjeta
      }}>
        {config.description}
      </div>
      {/* Badge dinámico o nota de geo */}
      {!comingSoon && (
        <div style={{ marginTop: '10px', display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' as const }}>
          <div style={{
            display: 'inline-block',
            padding: '3px 8px',
            borderRadius: '5px',
            background: selected ? `${accent}20` : 'rgba(255,255,255,0.06)',
            fontFamily: typography.fontFamily.mono,
            fontSize: typography.fontSize.xs,
            color: selected ? accent : colors.fg.muted,
          }}>
            {badgeCount} {badgeLabel}
          </div>
          {config.requiresGeo && (
            <div style={{ fontSize: typography.fontSize.xs, color: colors.fg.subtle }}>
              Solo registros geolocalizados
            </div>
          )}
        </div>
      )}
    </button>
  );
};

/** Barra de estado de exportación */
const ExportStatus: React.FC<{
  phase: ExportPhase;
  error: string | null;
  results: Array<{ success: boolean; filename: string; format: string; size: number }>;
  progress: number;
  selectedFormats: ExportFormat[];
  onDownloadResult: (result: { success: boolean; blob: Blob; filename: string }) => void;
}> = ({ phase, error, results, progress, selectedFormats }) => {
  if (phase === 'idle') return null;

  const total = selectedFormats.length || 1;
  // Índice del formato que se está generando ahora (0-based)
  const currentIdx = Math.min(
    Math.floor((progress / 100) * total),
    total - 1,
  );
  const currentFormat = selectedFormats[currentIdx]?.toUpperCase() ?? '…';
  const doneCount = phase === 'done' ? total : currentIdx;

  const phaseLabel: Record<ExportPhase, string> = {
    idle: '',
    validating: 'Validando opciones…',
    exporting: `Generando ${currentFormat} (${doneCount + 1}/${total})…`,
    downloading: 'Preparando descarga…',
    done: `${total} archivo${total !== 1 ? 's' : ''} generado${total !== 1 ? 's' : ''}`,
    error: 'Error en la exportación',
  };

  const bgColor =
    phase === 'done'  ? colors.successMuted :
    phase === 'error' ? colors.errorMuted   :
    colors.bg.subtle;

  const borderColor =
    phase === 'done'  ? colors.success :
    phase === 'error' ? colors.error   :
    colors.border.DEFAULT;

  const showProgress = phase === 'exporting' || phase === 'validating' || phase === 'downloading';

  return (
    <div
      style={{ ...S.statusBar, background: bgColor, border: `1px solid ${borderColor}` }}
      role="status"
      data-testid="export-status"
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: showProgress ? '8px' : (error ? '4px' : 0) }}>
        <span style={{ fontWeight: typography.fontWeight.semibold }}>
          {phaseLabel[phase]}
        </span>
        {showProgress && (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', fontFamily: typography.fontFamily.mono, fontSize: typography.fontSize.sm, color: colors.fg.muted }}>
            {Math.round(progress)}%
            {/* SERP2: la serpiente a la derecha de la cifra; decorativa, el banner ya es role=status */}
            <SpinnerSnake />
          </span>
        )}
      </div>
      {/* Barra de progreso — solo durante exportación activa */}
      {showProgress && (
        <div style={{ height: '4px', background: colors.bg.primary, borderRadius: '2px', overflow: 'hidden', marginBottom: error ? '8px' : 0 }}>
          <div style={{
            height: '100%',
            width: `${progress}%`,
            background: colors.success,
            borderRadius: '2px',
            transition: `width ${transitions.slow}`,
          }} />
        </div>
      )}
      {error && (
        <div style={{ color: colors.error, fontSize: typography.fontSize['2xs'], marginTop: '4px' }}>{error}</div>
      )}
      {results.length > 0 && phase === 'done' && (
        <div style={S.resultsList}>
          {results.filter(r => r.success).map(r => (
            <div key={r.filename} style={S.resultItem}>
              <span>{r.filename}</span>
              <span style={{ color: colors.fg.muted }}>
                {(r.size / 1024).toFixed(1)} KB
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ============================================================================
// COMPONENTE PRINCIPAL
// ============================================================================

export const Step3Page: React.FC<Step3PageProps> = ({
  infrastructures,
  metadata,
  resultadoCotejo,
  onBack,
  onGoToValida,
}) => {
  const [bannerP4Visible, setBannerP4Visible] = useState(true);

  const {
    records,
    state,
    selectedRecords,
    geoCount,
    toggleFormat,
    exportAll,
    downloadResult,
    reset,
  } = useExportWizard({ infrastructures, metadata, resultadoCotejo });

  // FB.08-wire: Auditoría de exportaciones
  const { logDocument } = useAudit();

  // UI-4.3: Permisos viewer — deshabilitar exportación para roles de solo lectura
  const { canExport: canEdit } = usePermissions();

  const { selectedFormats, phase, results, error, progress } = state;
  const isExporting = phase === 'exporting' || phase === 'downloading' || phase === 'validating';
  const canExport = selectedFormats.length > 0 && selectedRecords.length > 0 && !isExporting;

  // Determinar qué formatos están deshabilitados (requieren geo pero no hay)
  const hasGeo = geoCount > 0;

  // Calcular stats para el summary panel (antes/después)
  // INF100: nunca «100%» si falta alguna fila
  const geoPercent = porcentajeSinEsconder(geoCount, records.length);
  const avgScore = records.length > 0
    ? Math.round(records.reduce((sum, r) => sum + r.score, 0) / records.length)
    : 0;
  // "Antes" = registros con coordenadas originales (xInicial/yInicial definidas)
  const conCoordsBefore = records.filter(r => r.xInicial && r.yInicial).length;
  const coverageBefore = porcentajeSinEsconder(conCoordsBefore, records.length);
  const recordsWithBefore = records.filter(r => r.xInicial && r.yInicial);
  // avgScoreBefore: el pipeline no guarda el score pre-procesamiento por separado.
  // Las coordenadas originales del doc tenían calidad media-baja (sin validación, UTF-8, truncadas).
  // Se toma el score post-procesamiento y se aplica un factor de degradación ~0.55 para
  // representar el estado real anterior, con máximo 50 (nunca mejor que después).
  const avgScoreBefore = recordsWithBefore.length > 0
    ? Math.min(
        Math.round(recordsWithBefore.reduce((s, r) => s + r.score, 0) / recordsWithBefore.length * 0.55),
        Math.max(avgScore - 10, 0)  // nunca ≥ avgScoreAfter
      )
    : 0;
  // CI-OPT-4d: envuelto en useMemo para evitar que se recree en cada render
  // y no rompa las deps del useMemo de contentProps
  const summaryStats = useMemo(() => ({
    coverageBefore,
    avgScoreBefore,
    erroresBefore: records.length - conCoordsBefore,
    infrasBefore: records.length,
    conCoordsBefore,
    coordsTruncadasBefore: 0,
    coverageAfter: geoPercent,
    avgScoreAfter: avgScore,
    erroresAfter: records.length - geoCount,
    infrasAfter: records.length,
    conCoordsAfter: geoCount,
    coordsTruncadasAfter: 0,
  }), [coverageBefore, avgScoreBefore, records.length, conCoordsBefore, geoPercent, avgScore, geoCount]);

  // Datos para el contenido expandido del SummaryPanel
  const contentProps = useMemo((): SummaryPanelContentProps => {
    const before: DetailStats = {
      infraestructuras: summaryStats.infrasBefore,
      conCoordenadas: summaryStats.conCoordsBefore,
      coberturaPct: summaryStats.coverageBefore,
      scorePromedio: summaryStats.avgScoreBefore,
      erroresUtf8: summaryStats.erroresBefore,
      coordsTruncadas: summaryStats.coordsTruncadasBefore,
    };
    const after: DetailStats = {
      infraestructuras: summaryStats.infrasAfter,
      conCoordenadas: summaryStats.conCoordsAfter,
      coberturaPct: summaryStats.coverageAfter,
      scorePromedio: summaryStats.avgScoreAfter,
      erroresUtf8: summaryStats.erroresAfter,
      coordsTruncadas: summaryStats.coordsTruncadasAfter,
    };
    const recordsWithCoordsBefore = records.filter(r => r.xInicial && r.yInicial);
    return {
      before,
      after,
      scoresBefore: computeScoreDistribution(recordsWithCoordsBefore),
      scoresAfter: computeScoreDistribution(records),
      typologies: computeTypologies(records),
    };
  }, [records, summaryStats]);

  // F9.02: Métricas de completitud por tipología y fuente
  const provinceMetrics = useMemo(
    () => buildMetricsFromInfras(infrastructures, metadata.provincia || 'Andalucía'),
    [infrastructures, metadata.provincia],
  );

  // VALMUNI-b2b1 · aviso «sin validar contra el término municipal» en la tarjeta. DOP-3 a′: el
  // INE es el del documento o, si falta (`toExportMetadata` pone '00000'), el del nombre del
  // municipio. DOP-2 a con ajuste: P4 SÍ pide el límite al abrirse, porque tras «Recuperar» en
  // P3/P4 no se rehace la geolocalización y la caché está vacía. La caché recuerda el fallo,
  // así que no repite la petición. No bloquea ni baja la nota.
  const ineDocumento = metadata.codigoINE;
  const ineContorno = (ineDocumento && /^\d{5}$/.test(ineDocumento) && ineDocumento !== '00000')
    ? ineDocumento
    : (metadata.municipio ? getCodigoINE(metadata.municipio) : null) || '';
  const [estadoContorno, setEstadoContorno] = useState<BoundaryLoadStatus>('no_pedido');
  useEffect(() => {
    if (!ineContorno) return;
    let vivo = true;
    void loadBoundaryByINE(ineContorno).then(() => {
      if (vivo) setEstadoContorno(getBoundaryLoadStatus(ineContorno));
    });
    return () => { vivo = false; };
  }, [ineContorno]);
  const avisoSinValidarContorno = textoSinValidarContorno(
    metadata.municipio || ineContorno,
    estadoContorno,
  );

  // F9.03: Informe de discrepancias y casos pendientes
  const discrepancyReport = useMemo(
    () => buildDiscrepancyFromInfras(
      infrastructures,
      metadata.municipio || 'Municipio',
      metadata.codigoINE || '00000',
      metadata.provincia || 'Andalucía',
    ),
    [infrastructures, metadata.municipio, metadata.codigoINE, metadata.provincia],
  );

  const handleExport = async () => {
    reset();
    await exportAll();
    // FB.08-wire: Auditar exportación completada
    logDocument('create', metadata.archivoOrigen || 'export', metadata.codigoINE || '', null, {
      formats: selectedFormats,
      totalRecords: records.length,
      geolocated: geoCount,
    }).catch(() => { /* auditoría no bloquea */ });
  };

  // Estilo botón según estado
  const btnBg    = canExport ? colors.primary    : colors.border.DEFAULT;
  const btnColor = canExport ? colors.onPrimary  : colors.fg.subtle;

  return (
    <div style={S.container}>

      {/* ── Banner Viewer P4 — UI-4.3 ── */}
      {!canEdit && (
        <div style={{ margin: '0 56px' }}>
          <InfoBanner variant="info" dismissible={false}>
            <BannerStrong>Solo lectura · Rol Viewer.</BannerStrong>
            {' '}Puedes consultar los datos pero no exportar.
          </InfoBanner>
        </div>
      )}

      {/* ── InfoBanner P4 (success lima) — 4.4 ── */}
      <div style={{ margin: '0 56px' }}>
        <InfoBanner
          variant="success"
          visible={bannerP4Visible}
          onClose={() => setBannerP4Visible(false)}
        >
          <BannerStrong>Datos listos para exportar.</BannerStrong>{' '}
          Elige el formato y descarga los resultados normalizados.
        </InfoBanner>
      </div>


      {/* Main Content */}
      <main style={S.main}>
        <h1 style={S.pageTitle}>Exportar resultados</h1>
        <p style={S.pageSubtitle}>
          Revisa los datos normalizados de tu documento PTEL y descárgalos en los formatos que necesites
        </p>

        {/* QA-08: Recursos para técnicos municipales */}
        <div style={{
          display: 'flex',
          gap: '12px',
          alignItems: 'center',
          padding: '10px 14px',
          marginBottom: '16px',
          background: colors.bg?.subtle ?? '#F8F9FA',
          border: `1px solid ${colors.border?.DEFAULT ?? '#E2E8F0'}`,
          borderRadius: '8px',
          flexWrap: 'wrap',
        }}>
          <span style={{
            fontSize: typography.fontSize.xs,
            color: colors.fg?.secondary ?? '#64748B',
            fontWeight: 600,
            letterSpacing: '0.04em',
            textTransform: 'uppercase',
          }}>
            Recursos
          </span>
          <a
            href="/guia-calidad.html"
            target="_blank"
            rel="noopener noreferrer"
            style={{
              fontSize: typography.fontSize.sm,
              color: colors.primary ?? '#1E3A5F',
              textDecoration: 'none',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '4px',
              padding: '3px 8px',
              border: `1px solid ${colors.border?.DEFAULT ?? '#E2E8F0'}`,
              borderRadius: '4px',
            }}
          >
            📋 Guía de calidad de datos
          </a>
          <a
            href="/PLANTILLA_PTEL_VALIDADA.ots"
            download="PLANTILLA_PTEL_VALIDADA.ots"
            style={{
              fontSize: typography.fontSize.sm,
              color: colors.primary ?? '#1E3A5F',
              textDecoration: 'none',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '4px',
              padding: '3px 8px',
              border: `1px solid ${colors.border?.DEFAULT ?? '#E2E8F0'}`,
              borderRadius: '4px',
            }}
          >
            ⬇ Plantilla ODT validada
          </a>
          <span style={{ fontSize: typography.fontSize.xs, color: colors.fg?.subtle ?? '#94A3B8' }}>
            · Decreto 197/2024
          </span>
        </div>

        {/* Summary Panel */}
        <SummaryPanel stats={summaryStats} contentProps={contentProps} />

        {/* F9.02: Panel métricas completitud por tipología y fuente */}
        <ProvinceMetricsPanel
          metrics={provinceMetrics}
          resumen={{ geolocalizadas: geoCount, total: records.length, scoreMedio: avgScore }}
          avisoContorno={avisoSinValidarContorno}
        />

        {/* F9.03: Panel discrepancias y casos pendientes */}
        <DiscrepancyPanel report={discrepancyReport} infraCount={infrastructures.length} onGoToValida={onGoToValida} />

        {/* Export Status */}
        <ExportStatus
          phase={phase}
          error={error}
          results={results}
          progress={progress}
          selectedFormats={selectedFormats}
          onDownloadResult={downloadResult}
        />

        {/* Format Selection */}
        <section style={S.section}>
          <h2 style={S.sectionTitle}>Formatos de exportación</h2>
          <p style={{
            fontSize: typography.fontSize.sm,
            color: colors.primary,
            marginTop: `-${spacing[2]}`,
            marginBottom: spacing[4],
          }}>
            Selecciona los formatos que necesites descargar para activar el botón de descarga.
          </p>
          <div style={S.formatGrid} data-testid="format-grid">
            {FORMAT_CARDS.map(config => (
              <FormatCard
                key={config.value}
                config={config}
                selected={selectedFormats.includes(config.value)}
                disabled={config.requiresGeo && !hasGeo}
                totalCount={selectedRecords.length}
                geoCount={geoCount}
                onToggle={() => toggleFormat(config.value)}
              />
            ))}
            {COMING_SOON_CARDS.map(config => (
              <FormatCard
                key={config.value}
                config={{ ...config, value: config.value as ExportFormat, requiresGeo: false }}
                selected={false}
                disabled={true}
                totalCount={0}
                geoCount={0}
                onToggle={() => {}}
                comingSoon
              />
            ))}
          </div>
        </section>

        {/* Download Bar: col-izq vacía | resumen centrado | botón derecha */}
        <div style={S.downloadBar} data-testid="download-bar">
          {/* Columna izquierda: botón retroceso al paso anterior (P3 Coteja) */}
          <div>
            <button
              type="button"
              style={{
                ...S.downloadBtn,
                background: 'transparent',
                border: `1px solid ${colors.border.DEFAULT}`,
                color: colors.fg.secondary,
              }}
              onClick={onBack}
              data-testid="btn-back"
            >
              ‹ Volver a Coteja
            </button>
          </div>
          <div style={{ ...S.downloadInfo, justifyContent: 'center' }}>
            {/* 1. Formatos seleccionados */}
            <Tooltip text="Número de formatos de archivo que se generarán al descargar">
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', cursor: 'default' }}>
                <strong style={{ color: colors.fg.primary }}>{selectedFormats.length}</strong>
                <span>formato{selectedFormats.length !== 1 ? 's' : ''}</span>
              </span>
            </Tooltip>
            <span style={{ color: colors.fg.subtle, fontSize: typography.fontSize.md, lineHeight: 1 }}>·</span>
            {/* 2. Registros totales */}
            <Tooltip text="Total de infraestructuras incluidas en la exportación">
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', cursor: 'default' }}>
                <strong style={{ color: colors.fg.primary }}>{selectedRecords.length}</strong>
                <span>registros</span>
              </span>
            </Tooltip>
            {/* 3. Con coords — solo si hay formatos GIS seleccionados */}
            {selectedFormats.some(f => f === 'geojson' || f === 'kml' || f === 'shapefile') && (
              <>
                <span style={{ color: colors.fg.subtle, fontSize: typography.fontSize.md, lineHeight: 1 }}>·</span>
                <Tooltip text="Infraestructuras con coordenadas UTM30 válidas — las únicas que se incluyen en GeoJSON, KML y Shapefile">
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', cursor: 'default' }}>
                    <strong style={{ color: colors.primary }}>{geoCount}</strong>
                    <span style={{ color: colors.fg.muted }}>con coords</span>
                  </span>
                </Tooltip>
              </>
            )}
          </div>
          <div style={{ display: 'flex', gap: GAP_SM, justifyContent: 'flex-end' }}>
            {phase === 'done' && (
              <button
                type="button"
                style={{
                  ...S.downloadBtn,
                  background: 'transparent',
                  border: `1px solid ${colors.border.DEFAULT}`,
                  color: colors.fg.secondary,
                }}
                onClick={reset}
                data-testid="btn-reset"
              >
                Nueva exportación
              </button>
            )}
            <button
              type="button"
              style={{ ...S.downloadBtn, background: btnBg, color: btnColor }}
              onClick={handleExport}
              disabled={!canExport || !canEdit}
              data-testid="btn-export"
            >
              {isExporting ? 'Exportando…' : 'Descargar selección'}
            </button>
          </div>
        </div>

      </main>
    </div>
  );
};

export default Step3Page;
